import { Button } from '@repo/ui'
import * as Sentry from '@sentry/react'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { listConnectors } from '@/api/content/connector'
import type { ListConnectorsResponse } from '@/api/content/connector'
import { RagFileUploadState, fetchRagFiles } from '@/api/content/file'
import type { GetRagFileResponse, RagFile } from '@/api/content/file'
import { fetchRagTexts } from '@/api/content/text'
import type { GetRagTextResponse } from '@/api/content/text'
import {
  clearPresentationDefaultTemplate,
  clearProposalDefaultTemplate,
  getPresentationDefaultTemplate,
  getProposalDefaultTemplate,
  setPresentationDefaultTemplate,
  setProposalDefaultTemplate
} from '@/api/documentAgent/documentAgent'
import type { UserOrgProfile } from '@/api/user/profile'
import { ConnectorPage } from '@/components/ConnectorPage'
import { RagFilePage } from '@/components/RagFilePage'
import { RagTextPage } from '@/components/RagTextPage'
import { isProposalDocFile } from '@/components/proposal/proposalFiles'
import { SettingsSection } from './ScopedSettings'
import { rememberScopedSettingsReturn } from './settingsReturn'
import type { ContentSettingsKind, SettingsTarget } from './ScopedSettings'

const contentKinds: ReadonlyArray<{ value: ContentSettingsKind; label: string }> = [
  { value: 'files', label: 'Files' },
  { value: 'text', label: 'Text' },
  { value: 'connectors', label: 'Connectors' }
]

type ContentLoadResult =
  | {
      kind: 'files'
      data: GetRagFileResponse
      proposalDefaultDocId: number | null
      presentationDefaultDocId: number | null
    }
  | { kind: 'text'; data: GetRagTextResponse }
  | { kind: 'connectors'; data: ListConnectorsResponse }

export function ScopedContentSettings({
  projectId,
  userOrgProfile,
  kind,
  initialContentId,
  onKindChange,
  onAuthorityChanged
}: {
  projectId?: number
  userOrgProfile: UserOrgProfile
  kind: ContentSettingsKind
  initialContentId?: number
  onKindChange: (kind: ContentSettingsKind) => void
  onAuthorityChanged: () => void | Promise<void>
}) {
  const availableKinds = userOrgProfile.enabled_features.content_connectors
    ? contentKinds
    : contentKinds.filter((entry) => entry.value !== 'connectors')
  const activeKind = availableKinds.some((entry) => entry.value === kind) ? kind : 'files'

  return (
    <SettingsSection
      id="content-settings-heading"
      title={projectId === undefined ? 'Global content' : 'Workspace content'}
      summary={projectId === undefined ? 'Shared across the organisation.' : 'Available inside this workspace.'}
      action={
        <div
          role="tablist"
          aria-label="Content type"
          className="flex w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1 sm:w-fit"
        >
          {availableKinds.map((entry) => (
            <Button
              key={entry.value}
              type="button"
              role="tab"
              size="sm"
              variant={entry.value === activeKind ? 'secondary' : 'ghost'}
              aria-selected={entry.value === activeKind}
              onClick={() => onKindChange(entry.value)}
            >
              {entry.label}
            </Button>
          ))}
        </div>
      }
    >
      <ContentManager
        key={`${projectId ?? 'organisation'}:${activeKind}:${initialContentId ?? 'all'}`}
        kind={activeKind}
        {...(projectId !== undefined ? { projectId } : {})}
        {...(initialContentId !== undefined ? { initialContentId } : {})}
        userOrgProfile={userOrgProfile}
        onAuthorityChanged={onAuthorityChanged}
      />
    </SettingsSection>
  )
}

function ContentManager({
  kind,
  projectId,
  initialContentId,
  userOrgProfile,
  onAuthorityChanged
}: {
  kind: ContentSettingsKind
  projectId?: number
  initialContentId?: number
  userOrgProfile: UserOrgProfile
  onAuthorityChanged: () => void | Promise<void>
}) {
  const [page, setPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [focusedContentId, setFocusedContentId] = useState(initialContentId)
  const [refreshKey, setRefreshKey] = useState(0)
  const [result, setResult] = useState<ContentLoadResult>()
  const [error, setError] = useState<string>()

  const refreshAuthority = useCallback(async () => {
    await onAuthorityChanged()
    setRefreshKey((current) => current + 1)
  }, [onAuthorityChanged])

  const clearFocusedContent = () => {
    setFocusedContentId(undefined)
    setPage(1)
  }

  useEffect(() => {
    let cancelled = false

    const load = async (): Promise<ContentLoadResult> => {
      if (kind === 'files') {
        const params = {
          currentPage: page,
          searchText,
          ...(projectId !== undefined ? { projectId } : {}),
          ...(focusedContentId !== undefined ? { docId: focusedContentId } : {})
        }
        const includeDefaults = projectId === undefined
        const [data, proposalDefault, presentationDefault] = await Promise.all([
          fetchRagFiles(params),
          includeDefaults && userOrgProfile.enabled_features.proposal_agent
            ? getProposalDefaultTemplate()
            : Promise.resolve({ template: null }),
          includeDefaults && userOrgProfile.enabled_features.presentation_agent
            ? getPresentationDefaultTemplate()
            : Promise.resolve({ template: null })
        ])
        return {
          kind,
          data,
          proposalDefaultDocId: proposalDefault.template?.docId ?? null,
          presentationDefaultDocId: presentationDefault.template?.docId ?? null
        }
      }
      if (kind === 'text') {
        return {
          kind,
          data: await fetchRagTexts({
            currentPage: page,
            searchText,
            ...(projectId !== undefined ? { projectId } : {}),
            ...(focusedContentId !== undefined ? { docId: focusedContentId } : {})
          })
        }
      }
      return { kind, data: await listConnectors(projectId) }
    }

    void load()
      .then((nextResult) => {
        if (cancelled) return
        if (nextResult.kind !== 'connectors' && nextResult.data.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setResult(nextResult)
        setError(undefined)
      })
      .catch((loadError: unknown) => {
        if (cancelled) return
        Sentry.captureException(loadError, { extra: { kind, projectId, page, searchText } })
        setError(loadError instanceof Error ? loadError.message : 'Content could not be loaded.')
      })

    return () => {
      cancelled = true
    }
  }, [
    focusedContentId,
    kind,
    page,
    projectId,
    refreshKey,
    searchText,
    userOrgProfile.enabled_features.presentation_agent,
    userOrgProfile.enabled_features.proposal_agent
  ])

  if (result === undefined && error === undefined) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 rounded-lg border bg-muted/20 px-4 py-8 text-sm text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading content
      </div>
    )
  }

  if (error !== undefined) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 p-4">
        <p className="text-sm text-destructive">{error}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (result?.kind === 'files') {
    const assignmentControls =
      projectId === undefined
        ? [
            ...(userOrgProfile.enabled_features.proposal_agent
              ? [
                  {
                    assignedDocId: result.proposalDefaultDocId,
                    label: 'Default proposal template',
                    canManage: userOrgProfile.org.admin,
                    canAssign: (item: RagFile) =>
                      item.fileUploadState === RagFileUploadState.Complete && isProposalDocFile(item.fileName),
                    onAssign: async (fileId: number) => {
                      await setProposalDefaultTemplate(fileId)
                    },
                    onClear: clearProposalDefaultTemplate
                  }
                ]
              : []),
            ...(userOrgProfile.enabled_features.presentation_agent
              ? [
                  {
                    assignedDocId: result.presentationDefaultDocId,
                    label: 'Default presentation template',
                    canManage: userOrgProfile.org.admin,
                    canAssign: (item: RagFile) =>
                      item.fileUploadState === RagFileUploadState.Complete &&
                      item.fileName.toLowerCase().endsWith('.pptx'),
                    onAssign: async (fileId: number) => {
                      await setPresentationDefaultTemplate(fileId)
                    },
                    onClear: clearPresentationDefaultTemplate
                  }
                ]
              : [])
          ]
        : undefined
    return (
      <RagFilePage
        embedded
        breadcrumbs={[]}
        items={result.data.items}
        {...(projectId !== undefined ? { projectId } : {})}
        currentPage={page}
        pageCount={result.data.pageCount}
        documentCount={result.data.documentCount}
        searchText={searchText}
        {...(focusedContentId !== undefined ? { onClearDocId: clearFocusedContent } : {})}
        onPageChange={setPage}
        onSearchChange={(value) => {
          setSearchText(value)
          setPage(1)
        }}
        {...(assignmentControls !== undefined ? { assignmentControls } : {})}
        onAuthorityChanged={refreshAuthority}
      />
    )
  }

  if (result?.kind === 'text') {
    return (
      <RagTextPage
        embedded
        breadcrumbs={[]}
        items={result.data.items}
        {...(projectId !== undefined ? { projectId } : {})}
        currentPage={page}
        pageCount={result.data.pageCount}
        documentCount={result.data.documentCount}
        searchText={searchText}
        {...(focusedContentId !== undefined ? { onClearDocId: clearFocusedContent } : {})}
        onPageChange={setPage}
        onSearchChange={(value) => {
          setSearchText(value)
          setPage(1)
        }}
        onAuthorityChanged={refreshAuthority}
      />
    )
  }

  if (result?.kind === 'connectors') {
    const target: SettingsTarget =
      projectId === undefined
        ? {
            scope: 'organisation',
            section: 'content',
            contentKind: 'connectors',
            ...(focusedContentId !== undefined ? { contentItemId: focusedContentId } : {})
          }
        : {
            scope: 'workspace',
            projectId,
            section: 'content',
            contentKind: 'connectors',
            ...(focusedContentId !== undefined ? { contentItemId: focusedContentId } : {})
          }
    return (
      <ConnectorPage
        embedded
        breadcrumbs={[]}
        connectors={result.data.connectors}
        {...(projectId !== undefined ? { projectId } : {})}
        userOrgProfile={userOrgProfile}
        {...(focusedContentId !== undefined ? { initialConnectorId: focusedContentId } : {})}
        {...(focusedContentId !== undefined ? { onClearConnectorId: clearFocusedContent } : {})}
        onAuthorityChanged={refreshAuthority}
        beforeOAuthRedirect={() => rememberScopedSettingsReturn(target)}
      />
    )
  }

  return null
}
